import React, { useEffect, useState } from 'react';
import { MapPinIcon, PlusIcon, SearchIcon, FilterIcon, DownloadIcon, UploadIcon, XIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, BuildingIcon } from 'lucide-react';
import LocationList from '../components/locations/LocationList';
import LocationForm from '../components/locations/LocationForm';
import LocationFilters from '../components/locations/LocationFilters';
import { Location } from '../types/location';
const LocationManagement = () => {
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState({
    field: 'lastUsedDate',
    direction: 'desc' as 'asc' | 'desc'
  });
  // Mock data loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLocations([{
        id: '1',
        name: 'Main Distribution Center',
        addressLine1: '123 Warehouse Ave',
        addressLine2: 'Suite 100',
        city: 'Chicago',
        state: 'IL',
        zip: '60601',
        country: 'USA',
        contactName: 'John Smith',
        phone: '(312) 555-0123',
        email: 'john@example.com',
        locationType: 'Both',
        lastUsedDate: '2024-01-15',
        tags: ['Warehouse', 'High Volume'],
        coordinates: {
          lat: 41.8781,
          lng: -87.6298
        }
      }
      // Add more mock locations...
      ]);
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const handleSort = (field: string) => {
    setSortConfig({
      field,
      direction: sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };
  const handleBulkAction = (action: string) => {
    switch (action) {
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedLocations.length} locations?`)) {
          // Handle bulk delete
          console.log('Deleting locations:', selectedLocations);
        }
        break;
      case 'export':
        // Handle bulk export
        console.log('Exporting locations:', selectedLocations);
        break;
      default:
        break;
    }
  };
  return <div className="py-4" data-id="element-643">
      <div className="mb-6" data-id="element-644">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-645">
          Location Management
        </h1>
        <p className="text-gray-600" data-id="element-646">
          Manage your frequently used pickup and delivery locations
        </p>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-647">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-648">
          <div className="flex items-center" data-id="element-649">
            <div className="p-2 bg-primary-50 rounded-lg" data-id="element-650">
              <MapPinIcon size={20} className="text-primary-600" data-id="element-651" />
            </div>
            <div className="ml-4" data-id="element-652">
              <p className="text-sm font-medium text-gray-500" data-id="element-653">
                Total Locations
              </p>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-654">
                {locations.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-655">
          <div className="flex items-center" data-id="element-656">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-657">
              <MapPinIcon size={20} className="text-green-600" data-id="element-658" />
            </div>
            <div className="ml-4" data-id="element-659">
              <p className="text-sm font-medium text-gray-500" data-id="element-660">
                Pickup Locations
              </p>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-661">
                {locations.filter(loc => loc.locationType === 'Pickup' || loc.locationType === 'Both').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-662">
          <div className="flex items-center" data-id="element-663">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-664">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-665" />
            </div>
            <div className="ml-4" data-id="element-666">
              <p className="text-sm font-medium text-gray-500" data-id="element-667">
                Delivery Locations
              </p>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-668">
                {locations.filter(loc => loc.locationType === 'Delivery' || loc.locationType === 'Both').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-669">
          <div className="flex items-center" data-id="element-670">
            <div className="p-2 bg-amber-50 rounded-lg" data-id="element-671">
              <CheckIcon size={20} className="text-amber-600" data-id="element-672" />
            </div>
            <div className="ml-4" data-id="element-673">
              <p className="text-sm font-medium text-gray-500" data-id="element-674">
                Recently Used (30d)
              </p>
              <p className="text-2xl font-semibold text-gray-900" data-id="element-675">
                {locations.filter(loc => {
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return new Date(loc.lastUsedDate) > thirtyDaysAgo;
              }).length}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Actions Bar */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-676">
        <div className="flex items-center space-x-2" data-id="element-677">
          <div className="relative flex-1 md:w-80" data-id="element-678">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-679">
              <SearchIcon size={16} className="text-gray-400" data-id="element-680" />
            </div>
            <input type="text" placeholder="Search locations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-681" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-2 border rounded-md text-sm font-medium flex items-center transition-colors ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-682">
            <FilterIcon size={16} className="mr-2" data-id="element-683" />
            Filters
            {showFilters ? <ChevronUpIcon size={16} className="ml-2" data-id="element-684" /> : <ChevronDownIcon size={16} className="ml-2" data-id="element-685" />}
          </button>
        </div>
        <div className="flex space-x-2" data-id="element-686">
          <button onClick={() => setShowAddForm(true)} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center" data-id="element-687">
            <PlusIcon size={16} className="mr-2" data-id="element-688" />
            Add Location
          </button>
          <button onClick={() => {}} // Handle import
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center" data-id="element-689">
            <UploadIcon size={16} className="mr-2" data-id="element-690" />
            Import
          </button>
          <button onClick={() => {}} // Handle export
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center" data-id="element-691">
            <DownloadIcon size={16} className="mr-2" data-id="element-692" />
            Export
          </button>
        </div>
      </div>
      {/* Filters Panel */}
      {showFilters && <LocationFilters onClose={() => setShowFilters(false)} onChange={() => {}} data-id="element-693" />}
      {/* Location List */}
      <LocationList locations={locations} loading={loading} sortConfig={sortConfig} onSort={handleSort} selectedLocations={selectedLocations} onSelectionChange={setSelectedLocations} data-id="element-694" />
      {/* Add/Edit Location Form Modal */}
      {showAddForm && <LocationForm onClose={() => setShowAddForm(false)} data-id="element-695" />}
    </div>;
};
export default LocationManagement;